import React, { useRef, useState, useMemo, useEffect, MouseEvent } from 'react'
import { useTransition, animated } from '@react-spring/web'
import { Main, Container, Message, Button, Content, Life, LifeError } from './notificationStyles'
import { NotificationType } from '../../store/features/notificationsSlice';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

interface MessageHubProps {
  config?: {
    tension: number
    friction: number
    precision: number
    velocity: number
  }
  timeout?: number
  children: (add: AddFunction) => void
  removeNotification: (key: number) => void
}

export type AddFunction = (msg: string, type: NotificationType) => void

interface Item {
  key: number
  type: NotificationType
  msg: string
}

let id = 0

function NotificationHub({
  config = { tension: 125, friction: 20, precision: 0.1, velocity: 1 },
  timeout = 5000,
  children,
  removeNotification
}: MessageHubProps) {
  const refMap = useMemo(() => new WeakMap(), [])
  const cancelMap = useMemo(() => new WeakMap(), [])
  const [items, setItems] = useState<Item[]>([])

  const transitions = useTransition(items, {
    from: { opacity: 0, height: 0, life: '100%' },
    keys: (item: any) => item.key,
    enter: (item: any) => async (next, cancel) => {
      cancelMap.set(item, cancel)
      await next({ opacity: 1, height: refMap.get(item).offsetHeight })
      await next({ life: '0%' })
    },
    leave: [{ opacity: 0 }, { height: 0 }],
    onRest: (result: any, ctrl: any, item: any) => {
      setItems(state =>
        state.filter(i => {
          return i.key !== item.key
        })
      )
      removeNotification(item.key as number)
    },
    config: (item, index, phase) => key => phase === 'enter' && key === 'life' ? { duration: timeout } : config,
  })

  useEffect(() => {
    children((msg: string, type: NotificationType) => {
      setItems(state => [...state, { key: id++, msg, type: type }])
    })
  }, [])

  return (
    <Container className='flex flex-col gap-2'>
      {transitions(({ life, ...style }, item: Item) => (
        <Message style={style}>
          <div className='shadow-main'>
            <Content type={item.type} ref={(ref: HTMLDivElement) => ref && refMap.set(item, ref)}>
              {
                item.type === NotificationType.SUCCESS ?
                  <Life style={{ right: life }} /> :
                  item.type === NotificationType.ERROR ?
                    <LifeError style={{ right: life }} /> :
                    <Life style={{ right: life }} />
              }
              <div className='pr-1 flex-none'>
                {
                  item.type === NotificationType.SUCCESS ?
                    <CheckCircleOutlineOutlinedIcon style={{ width: '24px', height: '24px', color: 'green' }} />
                    :
                    <ErrorOutlineOutlinedIcon style={{ width: '24px', height: '24px', color: 'red' }} />
                }
              </div>
              <div className='flex-1 pb-2'>
                <p>{item.msg}</p>
              </div>
              <div className='flex-none'>
                <Button
                  onClick={(e: MouseEvent) => {
                    e.stopPropagation()
                    if (cancelMap.has(item) && life.get() !== '0%') cancelMap.get(item)()
                  }}>
                  <CloseOutlinedIcon style={{ width: '20px', height: '20px' }} />
                </Button>
              </div>
            </Content>
          </div>
        </Message>
      ))}
    </Container>
  )
}

export default NotificationHub;